import { useEffect, useCallback, useRef, useState } from 'react';
import { Order } from '../types';

export interface NotificationOptions {
    orders: Order[];
    enabled: boolean;
    clientsMap?: Map<string, string>; // id -> name
    authToken?: string | null;
    userId?: string | null;
}

export const useNotifications = ({ orders, enabled, clientsMap, authToken, userId }: NotificationOptions) => {
    const permissionRef = useRef<NotificationPermission | null>(null);
    const lastCheckRef = useRef<number>(0);
    const subscriptionRef = useRef<PushSubscription | null>(null);
    const [isOnline, setIsOnline] = useState(
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );
    const [notificationMode, setNotificationMode] = useState<'offline' | 'online'>('offline');

    // Écouter les changements de connectivité
    useEffect(() => {
        const handleOnline = () => {
            setIsOnline(true);
            setNotificationMode('online');
            console.log('[Notifications] Mode ONLINE activé');
        };
        
        const handleOffline = () => {
            setIsOnline(false);
            setNotificationMode('offline');
            console.log('[Notifications] Mode OFFLINE activé');
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Charger la permission depuis localStorage au montage
    useEffect(() => {
        const savedPermission = localStorage.getItem('notification-permission') as NotificationPermission | null;
        if (savedPermission === 'granted' || savedPermission === 'denied' || savedPermission === 'default') {
            permissionRef.current = savedPermission;
        }
    }, []);

    // Demander la permission et s'inscrire aux push notifications
    const requestPermission = useCallback(async () => {
        if (!('Notification' in window)) {
            console.log('Les notifications ne sont pas supportées');
            return 'denied';
        }

        if (Notification.permission === 'granted') {
            permissionRef.current = 'granted';
            localStorage.setItem('notification-permission', 'granted');
            
            // S'inscrire aux push notifications si ONLINE et Service Worker disponible
            if (isOnline && 'serviceWorker' in navigator && 'PushManager' in window) {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    const subscription = await registration.pushManager.getSubscription();
                    
                    if (!subscription) {
                        const newSubscription = await registration.pushManager.subscribe({
                            userVisibleOnly: true,
                        }).catch(() => null);
                        
                        if (newSubscription) {
                            subscriptionRef.current = newSubscription;
                            await sendSubscriptionToServer(newSubscription);
                        }
                    } else {
                        subscriptionRef.current = subscription;
                    }
                } catch (error) {
                    console.warn('Erreur inscription push notifications:', error);
                }
            }
            
            return 'granted';
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission();
            permissionRef.current = permission;
            localStorage.setItem('notification-permission', permission);
            
            if (permission === 'granted' && isOnline && 'serviceWorker' in navigator && 'PushManager' in window) {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    const newSubscription = await registration.pushManager.subscribe({
                        userVisibleOnly: true,
                    }).catch(() => null);
                    
                    if (newSubscription) {
                        subscriptionRef.current = newSubscription;
                        await sendSubscriptionToServer(newSubscription);
                    }
                } catch (error) {
                    console.warn('Erreur inscription push notifications:', error);
                }
            }
            
            return permission;
        }

        return Notification.permission;
    }, [isOnline]);

    // Envoyer la subscription au serveur
    const sendSubscriptionToServer = useCallback(async (subscription: PushSubscription) => {
        if (!authToken || !userId || !isOnline) return;
        
        try {
            const endpoint = subscription.endpoint;
            const auth = subscription.getKey('auth');
            const p256dh = subscription.getKey('p256dh');
            
            await fetch('/api/subscribe_notifications.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    endpoint,
                    auth: auth ? btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(auth)))) : null,
                    p256dh: p256dh ? btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(p256dh)))) : null,
                }),
            });
        } catch (error) {
            console.error('Erreur envoi subscription:', error);
        }
    }, [authToken, userId, isOnline]);

    // Envoyer une notification (locale, pas push)
    const sendNotification = useCallback((title: string, body: string, tag?: string) => {
        if (Notification.permission !== 'granted') return;

        try {
            const notification = new Notification(title, {
                body,
                icon: '/pwa-192x192.png',
                badge: '/pwa-192x192.png',
                tag: tag || 'deadline-reminder',
                requireInteraction: true,
            });

            notification.onclick = () => {
                window.focus();
                notification.close();
            };
        } catch (e) {
            console.error('Erreur notification:', e);
        }
    }, []);

    // Vérifier les échéances et envoyer des notifications
    const checkDeadlines = useCallback(() => {
        if (!enabled || Notification.permission !== 'granted') return;

        const now = Date.now();
        const oneHour = 60 * 60 * 1000;

        // Ne vérifier qu'une fois par heure
        if (now - lastCheckRef.current < oneHour) return;
        lastCheckRef.current = now;

        const upcomingOrders = orders.filter(order => {
            if (!order.dueDate || order.isDeleted) return false;
            if (order.status === 'delivered' || order.status === 'cancelled') return false;

            const dueDate = new Date(order.dueDate).getTime();
            const daysRemaining = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));

            return daysRemaining >= 0 && daysRemaining <= 3;
        });

        upcomingOrders.forEach(order => {
            const dueDate = new Date(order.dueDate!).getTime();
            const daysRemaining = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
            const clientName = clientsMap?.get(order.clientId) || 'un client';

            let message = '';
            if (daysRemaining === 0) {
                message = `⚠️ Livraison aujourd'hui pour ${clientName}: "${order.title}"`;
            } else if (daysRemaining === 1) {
                message = `⏰ Livraison demain pour ${clientName}: "${order.title}"`;
            } else {
                message = `📋 ${daysRemaining} jours restants pour livrer "${order.title}" (${clientName})`;
            }

            sendNotification('Rappel Carnet de Mesures', message, `order-${order.id}`);
        });
    }, [orders, enabled, clientsMap, sendNotification]);

    // Demander la permission au montage si activé
    useEffect(() => {
        if (enabled && !permissionRef.current) {
            requestPermission();
        }
    }, [enabled, requestPermission]);

    // Vérifier les échéances régulièrement (fonctionne offline ou online)
    useEffect(() => {
        if (!enabled) return;

        // Vérification initiale
        const initialCheck = setTimeout(() => {
            checkDeadlines();
        }, 5000); // Attendre 5 secondes après le chargement

        // Vérification toutes les heures
        const interval = setInterval(checkDeadlines, 60 * 60 * 1000);

        return () => {
            clearTimeout(initialCheck);
            clearInterval(interval);
        };
    }, [enabled, checkDeadlines]);

    return {
        requestPermission,
        sendNotification,
        checkDeadlines,
        permission: (typeof Notification !== 'undefined' && Notification.permission) || 'denied',
        notificationMode, // 'offline' ou 'online'
        isOnline,
    };
};
